import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Provider, Session } from '@supabase/supabase-js';
import { adminApi, ApiError } from '../api.ts';
import { getAdminSupabase } from '../supabase.ts';
import type { AdminUser } from '../types.ts';
import { setAdminAccessToken } from './adminToken.ts';
import { readDenyReason, type DenyReason } from './denyReason.ts';
import { clearSessionStart, markSessionStart } from './sessionTimeout.ts';

// Registered OIDC provider for the central l2u-identity login page. Supabase's
// Provider union does not include custom:* names, so the cast is required.
const SSO_PROVIDER = (import.meta.env.VITE_ADMIN_SSO_PROVIDER ??
  'custom:l2u-identity') as Provider;

type AdminAuthValue = {
  user: AdminUser | null;
  session: Session | null;
  loading: boolean;
  // Set when the API refused an authenticated user (403). Distinct from
  // `user === null`, which also covers "not signed in" — the two need opposite
  // treatment, so they cannot share a flag.
  accessDenied: { reason: DenyReason } | null;
  signInWithSso: () => Promise<void>;
  signOut: () => Promise<void>;
  reload: () => Promise<void>;
};

const AdminAuthContext = createContext<AdminAuthValue>({
  user: null,
  session: null,
  loading: true,
  accessDenied: null,
  signInWithSso: async () => {},
  signOut: async () => {},
  reload: async () => {},
});

export function AdminAuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState<{ reason: DenyReason } | null>(null);

  const loadUser = useCallback(async (currentSession: Session | null) => {
    if (!currentSession) {
      // No session means nothing has been refused. Clearing the flag here is
      // what stops a signed-out user being bounced back to /admin/no-access by
      // AdminShell's guard when they return to /admin.
      setAccessDenied(null);
      setUser(null);
      return;
    }
    try {
      const res = await adminApi.get<{ user: AdminUser }>('/api/auth/admin/me');
      setAccessDenied(null);
      setUser(res.user);
    } catch (err) {
      // 403 means the person is authenticated but refused, so the session is
      // deliberately KEPT. Signing out here would clear only THIS portal's
      // session while the central l2u-identity and Okta sessions survive — the
      // next sign-in click would round-trip silently and refuse again, a fast
      // bounce loop with no message (spec S10). Holding the session is also
      // what lets the refusal screen name who is signed in and offer a real
      // sign-out.
      if (err instanceof ApiError && err.status === 403) {
        setAccessDenied({ reason: readDenyReason(err.details) });
        setUser(null);
        return;
      }
      // 401 means the Supabase session itself is not usable. There is nothing
      // to preserve, so clear it rather than leaving the user half signed in.
      if (err instanceof ApiError && err.status === 401) {
        try {
          await getAdminSupabase().auth.signOut();
        } catch {
          // ignore
        }
        setAccessDenied(null);
        setUser(null);
        return;
      }
      // Anything else (a network blip, a 500) is not a refusal. Leave the flag
      // clear so the user lands on the login page rather than being told they
      // have no access when the truth is that the request never landed.
      setAccessDenied(null);
      setUser(null);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    const supabase = (() => {
      try {
        return getAdminSupabase();
      } catch {
        return null;
      }
    })();

    if (!supabase) {
      setLoading(false);
      return () => {};
    }

    void supabase.auth.getSession().then(async ({ data }) => {
      if (!mounted) return;
      const initial = data.session ?? null;
      setSession(initial);
      setAdminAccessToken(initial?.access_token ?? null);
      await loadUser(initial);
      if (mounted) setLoading(false);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((event, next) => {
      if (!mounted) return;
      if (event === 'SIGNED_IN') markSessionStart(Date.now());
      if (event === 'SIGNED_OUT') clearSessionStart();
      setSession(next ?? null);
      setAdminAccessToken(next?.access_token ?? null);
      void loadUser(next ?? null);
    });

    return () => {
      mounted = false;
      subscription.subscription.unsubscribe();
    };
  }, [loadUser]);

  // Starts the redirect to the central login page. skipBrowserRedirect keeps
  // navigation in our hands so a provider misconfiguration surfaces as a thrown
  // error rather than a silent bounce. Resolves only on failure to start; on
  // success the browser leaves this page.
  const signInWithSso = useCallback(async () => {
    const supabase = getAdminSupabase();
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: SSO_PROVIDER,
      options: {
        redirectTo: `${window.location.origin}/admin/sso/callback`,
        skipBrowserRedirect: true,
      },
    });
    if (error) throw error;
    if (!data?.url) throw new Error('Sign-in did not return a redirect URL.');
    window.location.href = data.url;
  }, []);

  const signOut = useCallback(async () => {
    try {
      const supabase = getAdminSupabase();
      await supabase.auth.signOut();
    } finally {
      setSession(null);
      setUser(null);
      // Cleared explicitly rather than relying on the SIGNED_OUT event, so a
      // sign-out taken FROM the refusal screen cannot leave a stale flag that
      // redirects the next visit to /admin straight back to /admin/no-access.
      setAccessDenied(null);
      setAdminAccessToken(null);
      clearSessionStart();
    }
  }, []);

  const reload = useCallback(async () => {
    await loadUser(session);
  }, [loadUser, session]);

  const value = useMemo<AdminAuthValue>(
    () => ({ user, session, loading, accessDenied, signInWithSso, signOut, reload }),
    [user, session, loading, accessDenied, signInWithSso, signOut, reload],
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth(): AdminAuthValue {
  return useContext(AdminAuthContext);
}
